import { useEffect, useMemo, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { doc, getDoc, onSnapshot } from "firebase/firestore";
import {
  COL_PIPELINE_DEALS,
  COL_TEMPLATES,
  CRM_BASE_URL,
  DEFAULT_TEMPLATE_ID,
  effectiveAutoAssignRole,
  resolveContact,
  type Checklist as ChecklistDoc,
  type ChecklistItem,
  type ChecklistTemplate,
  type PipelineDeal,
  type Section,
  type TemplateItem,
} from "@closing/shared";
import { db } from "../lib/firebase";
import {
  addItem,
  addSection,
  addSectionFromTemplate,
  deleteItem,
  deleteSection,
  moveItem,
  moveItemTo,
  moveSection,
  reassignItems,
  renameItem,
  renameSection,
  setItemAssignee,
  setItemCompleted,
  setItemAnswer,
  setItemDueOn,
  setSectionActive,
  todayYMD,
  watchChecklist,
} from "../lib/db";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useDirectory } from "../lib/directory";
import ItemRow from "../components/ItemRow";
import CommentsDrawer from "../components/CommentsDrawer";
import DocumentsPanel from "../components/DocumentsPanel";
import RecommendationsPanel from "../components/RecommendationsPanel";

export default function Checklist() {
  const { dealId = "" } = useParams();
  const [checklist, setChecklist] = useState<ChecklistDoc | null | undefined>(undefined);
  const [deal, setDeal] = useState<(PipelineDeal & { id: string }) | null>(null);

  useEffect(() => {
    if (!dealId) return;
    // Route change between two deals reuses this component; drop the previous
    // deal's records so nothing (e.g. the bulk reassign) acts on the old
    // checklist's item ids under the new deal id before its snapshots land.
    setChecklist(undefined);
    setDeal(null);
    const unsubCl = watchChecklist(dealId, setChecklist);
    const unsubDeal = onSnapshot(doc(db, COL_PIPELINE_DEALS, dealId), (snap) => {
      setDeal(snap.exists() ? { id: snap.id, ...(snap.data() as PipelineDeal) } : null);
    });
    return () => {
      unsubCl();
      unsubDeal();
    };
  }, [dealId]);

  if (checklist === undefined) {
    return <p className="eyebrow py-20 text-center">Loading…</p>;
  }
  if (checklist === null) {
    return <NoChecklist dealId={dealId} deal={deal} />;
  }
  return <ChecklistView dealId={dealId} checklist={checklist} deal={deal} />;
}

function NoChecklist({
  dealId,
  deal,
}: {
  dealId: string;
  deal: (PipelineDeal & { id: string }) | null;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function create() {
    setBusy(true);
    setError(null);
    try {
      await api.instantiateChecklist(dealId);
      // The snapshot listener will swap the view when the doc lands.
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setBusy(false);
    }
  }

  return (
    <div className="mx-auto max-w-md py-20 text-center">
      <p className="eyebrow text-signature">No checklist yet</p>
      <h1 className="mt-2 text-xl font-bold">
        {deal?.deal ?? dealId}
        {deal?.company ? <span className="font-normal text-muted"> · {deal.company}</span> : null}
      </h1>
      <p className="mt-3 text-sm text-ink-soft">
        Create this deal's closing checklist from the team template. Blank items auto-assign to
        the research contact{deal?.researchContact ? ` (${deal.researchContact})` : ""}.
      </p>
      {!deal?.researchContact?.trim() && (
        <p className="mt-3 rounded-md bg-flag-bg/60 px-3 py-2 font-mono text-[11px] text-flag">
          ⚠️ No Research Contact set in Deal Pipeline — research-contact items will be left
          unassigned. Consider setting it there first.
        </p>
      )}
      <button onClick={() => void create()} disabled={busy} className="btn-primary mt-6">
        {busy ? "Creating…" : "Create closing checklist"}
      </button>
      {error && <p className="mt-4 font-mono text-[11px] text-signature">{error}</p>}
      <p className="mt-6">
        <Link to="/" className="font-mono text-[11px] text-muted hover:text-ink">
          ← back to deals
        </Link>
      </p>
    </div>
  );
}

function ChecklistView({
  dealId,
  checklist,
  deal,
}: {
  dealId: string;
  checklist: ChecklistDoc;
  deal: (PipelineDeal & { id: string }) | null;
}) {
  const items = Object.values(checklist.items);

  const allSections = useMemo(
    () => [...checklist.sections].sort((a, b) => a.sortOrder - b.sortOrder),
    [checklist.sections],
  );
  // A section is deactivated when active === false (absent/true = active).
  const activeSections = useMemo(
    () => allSections.filter((s) => s.active !== false),
    [allSections],
  );
  const deactivatedSections = useMemo(
    () => allSections.filter((s) => s.active === false),
    [allSections],
  );
  const allSectionIds = useMemo(() => new Set(allSections.map((s) => s.id)), [allSections]);
  const activeSectionIds = useMemo(
    () => new Set(activeSections.map((s) => s.id)),
    [activeSections],
  );
  // Safety net: items whose section no longer exists (e.g. a section deleted
  // concurrently) render under a synthetic "Unsectioned" block instead of
  // silently disappearing while still counting toward progress.
  const orphanItems = useMemo(
    () =>
      items
        .filter((i) => !allSectionIds.has(i.sectionId))
        .sort((a, b) => a.sortOrder - b.sortOrder),
    [items, allSectionIds],
  );
  // Progress reflects only what's visible: items in active sections plus
  // orphans. Items in deactivated sections are hidden and don't count.
  const visibleItems = useMemo(
    () => items.filter((i) => activeSectionIds.has(i.sectionId) || !allSectionIds.has(i.sectionId)),
    [items, activeSectionIds, allSectionIds],
  );
  const done = visibleItems.filter((i) => i.completed).length;
  const today = todayYMD();
  const overdue = visibleItems.filter((i) => !i.completed && i.dueOn && i.dueOn < today).length;
  const pct = visibleItems.length ? Math.round((done / visibleItems.length) * 100) : 0;
  // The whole-checklist delete wipes everything, including hidden items, so
  // its prompt counts all items (not just the visible-progress subset).
  const totalDone = items.filter((i) => i.completed).length;

  // Default template, loaded once, backs the "pre-fill from template" options
  // in the add-section control. Null while loading or if it's missing.
  const [template, setTemplate] = useState<ChecklistTemplate | null>(null);
  useEffect(() => {
    getDoc(doc(db, COL_TEMPLATES, DEFAULT_TEMPLATE_ID))
      .then((snap) => setTemplate(snap.exists() ? (snap.data() as ChecklistTemplate) : null))
      .catch(() => setTemplate(null));
  }, []);

  const [tab, setTab] = useState<"checklist" | "documents" | "ai">("checklist");
  const [commentsItemId, setCommentsItemId] = useState<string | null>(null);
  const commentsItem = commentsItemId ? checklist.items[commentsItemId] : undefined;

  // Deep link from notifications: /deals/:dealId?item=<id> opens that
  // item's comment thread once, then clears the param.
  const [searchParams, setSearchParams] = useSearchParams();
  const linkedItemId = searchParams.get("item");
  useEffect(() => {
    if (linkedItemId && checklist.items[linkedItemId]) {
      setCommentsItemId(linkedItemId);
      setSearchParams({}, { replace: true });
    }
  }, [linkedItemId, checklist.items, setSearchParams]);

  return (
    <div>
      {/* Header */}
      <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="eyebrow">
            <Link to="/" className="hover:text-ink">
              Deals
            </Link>{" "}
            / <span className="text-signature">{checklist.dealName}</span>
          </p>
          <h1 className="mt-1 text-2xl font-bold tracking-tight">
            {checklist.dealName}
            {checklist.company && (
              <span className="ml-2 font-normal text-muted">· {checklist.company}</span>
            )}
          </h1>
        </div>
        <div className="flex items-center gap-4">
          {overdue > 0 && (
            <span className="badge bg-flag-bg text-flag">
              {overdue} overdue
            </span>
          )}
          <span className="font-mono text-sm tabular-nums text-ink-soft">
            {done}/{visibleItems.length}
          </span>
          <a
            href={`${CRM_BASE_URL}/?deal=${encodeURIComponent(dealId)}`}
            target="_blank"
            rel="noreferrer"
            className="btn-secondary text-xs"
          >
            Open in Deal Pipeline ↗
          </a>
          <ReassignResearchButton
            dealId={dealId}
            checklist={checklist}
            deal={deal}
            currentTemplate={template}
          />
          <DeleteChecklistButton
            dealId={dealId}
            dealName={checklist.dealName}
            done={totalDone}
            total={items.length}
          />
        </div>
      </div>

      {/* Progress bar */}
      <div className="mb-6 h-1.5 overflow-hidden rounded-full bg-line">
        <div
          className={`h-full rounded-full transition-all ${pct === 100 ? "bg-positive" : "bg-signature"}`}
          style={{ width: `${pct}%` }}
        />
      </div>

      {/* Tabs */}
      <div className="mb-6 flex gap-1 border-b border-line">
        {(
          [
            ["checklist", "Checklist"],
            ["documents", "Documents"],
            ["ai", "AI Recommendations"],
          ] as const
        ).map(([key, label]) => (
          <button
            key={key}
            onClick={() => setTab(key)}
            className={`-mb-px border-b-2 px-4 py-2 font-mono text-[11px] uppercase tracking-[0.08em] transition-colors ${
              tab === key
                ? "border-signature text-ink"
                : "border-transparent text-muted hover:text-ink"
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {tab === "documents" && <DocumentsPanel dealId={dealId} />}
      {tab === "ai" && <RecommendationsPanel dealId={dealId} checklist={checklist} />}

      {/* Sections */}
      <div className={tab === "checklist" ? "space-y-6" : "hidden"}>
        {activeSections.map((section, idx) => (
          <SectionBlock
            key={section.id}
            dealId={dealId}
            checklist={checklist}
            section={section}
            isFirst={idx === 0}
            isLast={idx === activeSections.length - 1}
            items={items
              .filter((i) => i.sectionId === section.id)
              .sort((a, b) => a.sortOrder - b.sortOrder)}
            onComments={setCommentsItemId}
          />
        ))}

        {orphanItems.length > 0 && (
          <SectionBlock
            dealId={dealId}
            checklist={checklist}
            section={{ id: "__orphans__", name: "Unsectioned", sortOrder: 9999 }}
            items={orphanItems}
            onComments={setCommentsItemId}
          />
        )}

        <AddSection dealId={dealId} checklist={checklist} template={template} />

        {deactivatedSections.length > 0 && (
          <DeactivatedSections
            dealId={dealId}
            checklist={checklist}
            sections={deactivatedSections}
          />
        )}
      </div>

      {commentsItem && (
        <CommentsDrawer
          checklist={checklist}
          dealId={dealId}
          item={commentsItem}
          onClose={() => setCommentsItemId(null)}
        />
      )}
    </div>
  );
}

/**
 * Bulk do-over for the research-contact auto-assign: every item whose
 * auto-assignment ran through the "research" role (stored on the item, or
 * derived from the template for older checklists) gets one chosen assignee.
 * Fixed-assignee, legal, manual and AI items are never touched. Defaults to
 * whoever the deal's current Research Contact resolves to, so the common
 * case — the label didn't match at create time, or the contact changed — is
 * one click. Completed items are left alone unless asked, since re-attributing
 * finished work is usually not what "reassign" means.
 */
function ReassignResearchButton({
  dealId,
  checklist,
  deal,
  currentTemplate,
}: {
  dealId: string;
  checklist: ChecklistDoc;
  deal: (PipelineDeal & { id: string }) | null;
  /** The default template's current doc (already loaded by the page). */
  currentTemplate: ChecklistTemplate | null;
}) {
  const directory = useDirectory();

  // Role fallback for items created before `autoAssignRole` was stamped.
  // Instantiated items resolve against the template *as it was when this
  // checklist was created* (closing-templates/{id}/versions/{n}) — a role
  // edited since, or a non-default template, would otherwise misclassify
  // them. Items from sections copied in later resolve against the current
  // default instead (see effectiveAutoAssignRole).
  const [instantiatedTemplate, setInstantiatedTemplate] = useState<ChecklistTemplate | null>(
    null,
  );
  const needsTemplate = useMemo(
    () => Object.values(checklist.items).some((i) => i.autoAssignRole === undefined),
    [checklist.items],
  );
  useEffect(() => {
    if (!needsTemplate) return;
    const templateId = checklist.templateId ?? DEFAULT_TEMPLATE_ID;
    const templateRef = doc(db, COL_TEMPLATES, templateId);
    const version = checklist.templateVersion;
    let cancelled = false;
    (async () => {
      // Snapshot first; the template's current doc if that's missing
      // (pre-versioning imports) or it's a non-default template.
      let snap =
        version != null ? await getDoc(doc(templateRef, "versions", String(version))) : null;
      if (!snap?.exists()) snap = await getDoc(templateRef);
      if (!cancelled) {
        setInstantiatedTemplate(snap.exists() ? (snap.data() as ChecklistTemplate) : null);
      }
    })().catch(() => {
      if (!cancelled) setInstantiatedTemplate(null);
    });
    return () => {
      cancelled = true;
    };
  }, [needsTemplate, checklist.templateId, checklist.templateVersion]);
  const templates = useMemo(
    () => ({ instantiated: instantiatedTemplate, current: currentTemplate }),
    [instantiatedTemplate, currentTemplate],
  );
  const templatesLoading = needsTemplate && !instantiatedTemplate && !currentTemplate;
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [includeCompleted, setIncludeCompleted] = useState(false);
  const [targetEmail, setTargetEmail] = useState<string | null>(null);

  const researchItems = useMemo(
    () =>
      Object.values(checklist.items).filter(
        (i) => effectiveAutoAssignRole(i, templates) === "research",
      ),
    [checklist.items, templates],
  );
  const openItems = researchItems.filter((i) => !i.completed);
  const affected = includeCompleted ? researchItems : openItems;

  // Who the deal's Research Contact resolves to today — the default target.
  const suggested = useMemo(
    () => resolveContact(deal?.researchContact, directory),
    [deal?.researchContact, directory],
  );
  // Only apply the suggestion while the panel is opening, so the user's own
  // pick isn't overwritten by a directory snapshot arriving mid-edit.
  function openPanel() {
    setTargetEmail(suggested?.email ?? null);
    setIncludeCompleted(false);
    setOpen(true);
  }

  // Current assignee breakdown, for the "what am I changing" line.
  const current = useMemo(() => {
    const counts = new Map<string, number>();
    for (const i of affected) {
      const k = i.assigneeName ?? "Unassigned";
      counts.set(k, (counts.get(k) ?? 0) + 1);
    }
    return [...counts.entries()].sort((a, b) => b[1] - a[1]);
  }, [affected]);

  const target = targetEmail ? (directory.find((t) => t.email === targetEmail) ?? null) : null;

  async function apply() {
    setBusy(true);
    try {
      await reassignItems(
        dealId,
        affected.map((i) => i.id),
        target ? { email: target.email, displayName: target.displayName } : null,
        { skipCompleted: !includeCompleted },
      );
      setOpen(false);
    } catch (err) {
      window.alert(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="relative">
      <button
        onClick={() => (open ? setOpen(false) : openPanel())}
        className="btn-secondary text-xs"
        title="Reassign every research-contact item to one person"
      >
        Reassign research
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          <div className="absolute right-0 z-20 mt-1 w-80 rounded-md border border-line bg-white p-4 text-left shadow-lg">
            <p className="eyebrow">Reassign research items</p>
            {researchItems.length === 0 ? (
              <p className="mt-2 text-sm text-ink-soft">
                No research-contact items on this checklist
                {templatesLoading ? " (template still loading)" : ""}.
              </p>
            ) : (
              <>
                <p className="mt-2 text-sm text-ink-soft">
                  {affected.length} of {researchItems.length} research item
                  {researchItems.length === 1 ? "" : "s"}
                  {includeCompleted ? "" : ` (${researchItems.length - openItems.length} completed skipped)`}
                  .
                </p>
                {current.length > 0 && (
                  <p className="mt-1 font-mono text-[11px] text-muted">
                    Now: {current.map(([name, n]) => `${name} ×${n}`).join(", ")}
                  </p>
                )}
                <label className="mt-3 block">
                  <span className="eyebrow">Assign to</span>
                  <select
                    value={targetEmail ?? ""}
                    onChange={(e) => setTargetEmail(e.target.value || null)}
                    className="mt-1 w-full rounded-md border border-line bg-white px-2 py-1.5 text-sm"
                  >
                    <option value="">Unassigned</option>
                    {directory.map((t) => (
                      <option key={t.email} value={t.email}>
                        {t.displayName}
                      </option>
                    ))}
                  </select>
                </label>
                {deal?.researchContact?.trim() && !suggested && (
                  <p className="mt-2 font-mono text-[11px] text-flag">
                    Deal Pipeline's Research Contact "{deal.researchContact.split(/[,&]/)[0]!.trim()}"
                    didn't match anyone in the directory.
                  </p>
                )}
                <label className="mt-3 flex items-center gap-2 text-sm text-ink-soft">
                  <input
                    type="checkbox"
                    checked={includeCompleted}
                    onChange={(e) => setIncludeCompleted(e.target.checked)}
                  />
                  Include completed items
                </label>
                <div className="mt-4 flex justify-end gap-2">
                  <button onClick={() => setOpen(false)} className="btn-secondary text-xs">
                    Cancel
                  </button>
                  <button
                    onClick={() => void apply()}
                    disabled={busy || affected.length === 0}
                    className="btn-primary text-xs"
                  >
                    {busy
                      ? "Reassigning…"
                      : `Reassign ${affected.length} to ${target?.displayName ?? "Unassigned"}`}
                  </button>
                </div>
              </>
            )}
          </div>
        </>
      )}
    </div>
  );
}

/**
 * Admin-only: permanently deletes the checklist (items, progress, comments),
 * returning the deal to the "create checklist" state — the snapshot listener
 * flips this page to the create view when the doc disappears. Documents and
 * AI runs stay attached to the deal.
 */
function DeleteChecklistButton({
  dealId,
  dealName,
  done,
  total,
}: {
  dealId: string;
  dealName: string;
  done: number;
  total: number;
}) {
  const { isAdmin } = useAuth();
  const [busy, setBusy] = useState(false);
  if (!isAdmin) return null;

  async function handleDelete() {
    const ok = window.confirm(
      `Delete the ${dealName} checklist?\n\nThis permanently removes all ${total} items (${done} completed) and their comments — the deal goes back to the "create checklist" state. Uploaded documents and AI runs are kept.`,
    );
    if (!ok) return;
    setBusy(true);
    try {
      await api.deleteChecklist(dealId);
      // watchChecklist sees the doc vanish and swaps in the create view.
    } catch (err) {
      window.alert(err instanceof Error ? err.message : String(err));
      setBusy(false);
    }
  }

  return (
    <button
      onClick={() => void handleDelete()}
      disabled={busy}
      className="rounded-md border border-line px-3 py-2 font-mono text-[11px] uppercase tracking-[0.08em] text-muted transition-colors hover:border-flag hover:bg-flag-bg hover:text-flag disabled:opacity-50"
      title="Delete this checklist (admin)"
    >
      {busy ? "Deleting…" : "Delete"}
    </button>
  );
}

function SectionBlock({
  dealId,
  checklist,
  section,
  items,
  isFirst,
  isLast,
  onComments,
}: {
  dealId: string;
  checklist: ChecklistDoc;
  section: Section;
  items: ChecklistItem[];
  isFirst?: boolean;
  isLast?: boolean;
  onComments: (itemId: string) => void;
}) {
  const [collapsed, setCollapsed] = useState(false);
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState("");
  const [renaming, setRenaming] = useState(false);
  const [nameDraft, setNameDraft] = useState(section.name);
  const [dropTarget, setDropTarget] = useState<string | "end" | null>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const done = items.filter((i) => i.completed).length;

  function permanentlyDelete() {
    const msg =
      items.length === 0
        ? `Permanently delete the empty section "${section.name}"?`
        : `Permanently delete the section "${section.name}" AND its ${items.length} item(s) (${done} completed)?\n\nThis removes those items and their progress for good. To keep them for later, use Deactivate instead.`;
    if (!window.confirm(msg)) return;
    void deleteSection(dealId, section.id, {
      withItems: true,
      // Bind the delete to exactly what the user confirmed.
      confirmedItemIds: items.map((i) => i.id),
    }).catch((err) => window.alert(err instanceof Error ? err.message : String(err)));
  }

  async function submitNew() {
    const name = draft.trim();
    setDraft("");
    if (name) await addItem(dealId, checklist, section.id, name);
  }

  function handleDrop(e: React.DragEvent, beforeItemId: string | null) {
    e.preventDefault();
    e.stopPropagation();
    setDropTarget(null);
    const itemId = e.dataTransfer.getData("text/plain");
    if (itemId) void moveItemTo(dealId, checklist, itemId, section.id, beforeItemId);
  }

  return (
    <section
      className="overflow-hidden rounded-[10px] border border-line bg-white"
      // Dropping on the section (below its rows / on the header) appends.
      onDragOver={(e) => {
        e.preventDefault();
        setDropTarget("end");
      }}
      onDragLeave={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget as Node)) setDropTarget(null);
      }}
      onDrop={(e) => handleDrop(e, null)}
    >
      <header className="flex items-center justify-between gap-3 border-b border-line-strong bg-paper px-4 py-2">
        <button
          onClick={() => setCollapsed(!collapsed)}
          className="flex min-w-0 items-center gap-2 text-left"
        >
          <span className="font-mono text-[10px] text-muted">{collapsed ? "▸" : "▾"}</span>
          {renaming ? (
            <input
              autoFocus
              value={nameDraft}
              onChange={(e) => setNameDraft(e.target.value)}
              onClick={(e) => e.stopPropagation()}
              onBlur={() => {
                setRenaming(false);
                const name = nameDraft.trim();
                if (name && name !== section.name)
                  void renameSection(dealId, checklist, section.id, name);
                else setNameDraft(section.name);
              }}
              onKeyDown={(e) => e.key === "Enter" && (e.target as HTMLInputElement).blur()}
              className="border-b border-line-strong bg-transparent text-[11px] font-bold uppercase tracking-[0.08em] outline-none"
            />
          ) : (
            <span
              onDoubleClick={(e) => {
                e.stopPropagation();
                setRenaming(true);
              }}
              className="truncate font-sans text-[11px] font-bold uppercase tracking-[0.08em] text-ink-soft"
              title="Double-click to rename"
            >
              {section.name}
            </span>
          )}
        </button>
        <div className="flex items-center gap-3">
          <span className="font-mono text-[10px] tabular-nums text-muted">
            {done}/{items.length}
          </span>
          {/* Reorder arrows — only for real sections (not the orphan block). */}
          {isFirst !== undefined && (
            <span className="flex items-center gap-1">
              <button
                onClick={() => void moveSection(dealId, checklist, section.id, "up")}
                disabled={isFirst}
                className="flex h-5 w-5 items-center justify-center rounded font-mono text-[11px] text-muted hover:bg-line disabled:opacity-20"
                title="Move section up"
              >
                ↑
              </button>
              <button
                onClick={() => void moveSection(dealId, checklist, section.id, "down")}
                disabled={isLast}
                className="flex h-5 w-5 items-center justify-center rounded font-mono text-[11px] text-muted hover:bg-line disabled:opacity-20"
                title="Move section down"
              >
                ↓
              </button>
            </span>
          )}
          {/* Actions menu — real sections only (the orphan block isn't in
              sections). Deactivate hides+preserves; delete is permanent. */}
          {isFirst !== undefined && (
            <div className="relative">
              <button
                onClick={() => setMenuOpen((v) => !v)}
                className="flex h-5 w-5 items-center justify-center rounded font-mono text-[13px] leading-none text-muted hover:bg-line hover:text-ink"
                title="Section actions"
                aria-haspopup="menu"
                aria-expanded={menuOpen}
              >
                ⋯
              </button>
              {menuOpen && (
                <>
                  <div className="fixed inset-0 z-10" onClick={() => setMenuOpen(false)} />
                  <div className="absolute right-0 z-20 mt-1 w-56 overflow-hidden rounded-md border border-line bg-white py-1 text-left shadow-lg">
                    <button
                      onClick={() => {
                        setMenuOpen(false);
                        void setSectionActive(dealId, section.id, false).catch((err) =>
                          window.alert(err instanceof Error ? err.message : String(err)),
                        );
                      }}
                      className="block w-full px-3 py-1.5 text-left text-[12px] text-ink hover:bg-paper"
                    >
                      Deactivate
                      <span className="block font-mono text-[10px] text-muted">
                        hide &amp; keep — restore later
                      </span>
                    </button>
                    <button
                      onClick={() => {
                        setMenuOpen(false);
                        permanentlyDelete();
                      }}
                      className="block w-full px-3 py-1.5 text-left text-[12px] text-flag hover:bg-flag-bg"
                    >
                      Delete permanently
                      <span className="block font-mono text-[10px] text-flag/70">
                        removes items &amp; progress
                      </span>
                    </button>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </header>

      {!collapsed && (
        <div className={dropTarget === "end" ? "bg-paper/80" : ""}>
          {items.map((item, idx) => (
            <ItemRow
              key={item.id}
              item={item}
              blockedBy={(item.dependsOn ?? [])
                .map((id) => checklist.items[id])
                .filter((dep) => dep && !dep.completed)
                .map((dep) => dep!.name)}
              isFirst={idx === 0}
              isLast={idx === items.length - 1}
              dropIndicator={dropTarget === item.id}
              onDragStart={(e) => {
                e.dataTransfer.setData("text/plain", item.id);
                e.dataTransfer.effectAllowed = "move";
              }}
              onDragOver={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setDropTarget(item.id);
              }}
              onDrop={(e) => handleDrop(e, item.id)}
              onToggle={(c) => void setItemCompleted(dealId, item.id, c)}
              onRename={(name) => void renameItem(dealId, item.id, name)}
              onAssign={(a) => void setItemAssignee(dealId, item.id, a)}
              onDue={(d) => void setItemDueOn(dealId, item.id, d)}
              onAnswer={(a) => void setItemAnswer(dealId, item.id, a)}
              onMove={(dir) => void moveItem(dealId, checklist, item.id, dir)}
              onDelete={() => {
                if (window.confirm(`Delete "${item.name}"?`)) void deleteItem(dealId, item.id);
              }}
              onComments={() => onComments(item.id)}
            />
          ))}
          {adding ? (
            <form
              onSubmit={(e) => {
                e.preventDefault();
                void submitNew();
              }}
              className="border-t border-line px-4 py-2"
            >
              <input
                autoFocus
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onBlur={() => {
                  void submitNew();
                  setAdding(false);
                }}
                placeholder="New item — Enter to add"
                className="w-full bg-transparent text-[13px] outline-none placeholder:text-faint"
              />
            </form>
          ) : (
            <button
              onClick={() => setAdding(true)}
              className="block w-full px-4 py-2 text-left font-mono text-[11px] text-muted hover:bg-paper/60 hover:text-ink"
            >
              + add item
            </button>
          )}
        </div>
      )}
    </section>
  );
}

function alertErr(err: unknown) {
  window.alert(err instanceof Error ? err.message : String(err));
}

/**
 * Add-section control: a blank named section (as before), plus one-click
 * "pre-fill from template" options. Each option copies a template section's
 * heading and its items. Template sections already present in the checklist
 * (active or deactivated, matched by name) are omitted — reactivate those
 * from the deactivated area instead of duplicating them.
 */
function AddSection({
  dealId,
  checklist,
  template,
}: {
  dealId: string;
  checklist: ChecklistDoc;
  template: ChecklistTemplate | null;
}) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState("");

  const existingNames = useMemo(
    () => new Set(checklist.sections.map((s) => s.name.trim().toLowerCase())),
    [checklist.sections],
  );
  const templateOptions = useMemo(() => {
    if (!template) return [] as { section: Section; items: TemplateItem[] }[];
    return [...template.sections]
      .sort((a, b) => a.sortOrder - b.sortOrder)
      .filter((s) => !existingNames.has(s.name.trim().toLowerCase()))
      .map((s) => ({
        section: s,
        items: template.items.filter((t) => t.sectionId === s.id),
      }));
  }, [template, existingNames]);

  function addBlank() {
    const name = draft.trim();
    if (name) void addSection(dealId, checklist, name).catch(alertErr);
    setDraft("");
    setOpen(false);
  }

  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        className="font-mono text-[11px] uppercase tracking-[0.1em] text-muted hover:text-ink"
      >
        + add section
      </button>
    );
  }

  return (
    <div className="rounded-[10px] border border-line bg-white p-3">
      <form
        onSubmit={(e) => {
          e.preventDefault();
          addBlank();
        }}
        className="flex items-center gap-2"
      >
        <input
          autoFocus
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => e.key === "Escape" && setOpen(false)}
          placeholder="New section name…"
          className="flex-1 rounded-md border border-line bg-white px-3 py-2 text-sm outline-none focus:border-line-strong"
        />
        <button type="submit" className="btn-primary text-xs">
          Add
        </button>
        <button
          type="button"
          onClick={() => {
            setDraft("");
            setOpen(false);
          }}
          className="font-mono text-[11px] text-muted hover:text-ink"
        >
          cancel
        </button>
      </form>

      {templateOptions.length > 0 && (
        <div className="mt-3 border-t border-line pt-3">
          <p className="eyebrow mb-2 text-muted">or pre-fill from template</p>
          <div className="flex flex-wrap gap-2">
            {templateOptions.map(({ section, items }) => (
              <button
                key={section.id}
                onClick={() => {
                  void addSectionFromTemplate(dealId, checklist, section.name, items).catch(
                    alertErr,
                  );
                  setOpen(false);
                }}
                className="rounded-md border border-line px-3 py-1.5 text-left text-[12px] text-ink hover:border-line-strong hover:bg-paper"
                title={`Add "${section.name}" with its ${items.length} template item(s)`}
              >
                {section.name}
                <span className="ml-1.5 font-mono text-[10px] text-muted">
                  {items.length} item{items.length === 1 ? "" : "s"}
                </span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Collapsed drawer of deactivated sections. Each preserves its items (hidden
 * from the main list and excluded from progress) and can be reactivated in
 * place or permanently deleted.
 */
function DeactivatedSections({
  dealId,
  checklist,
  sections,
}: {
  dealId: string;
  checklist: ChecklistDoc;
  sections: Section[];
}) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="rounded-[10px] border border-dashed border-line bg-paper/40">
      <button
        onClick={() => setExpanded((v) => !v)}
        className="flex w-full items-center gap-2 px-4 py-2 text-left"
      >
        <span className="font-mono text-[10px] text-muted">{expanded ? "▾" : "▸"}</span>
        <span className="font-mono text-[11px] uppercase tracking-[0.08em] text-muted">
          Deactivated sections · {sections.length}
        </span>
      </button>
      {expanded && (
        <div className="divide-y divide-line border-t border-line">
          {sections.map((s) => {
            const secItems = Object.values(checklist.items).filter((i) => i.sectionId === s.id);
            const done = secItems.filter((i) => i.completed).length;
            return (
              <div key={s.id} className="flex items-center justify-between gap-3 px-4 py-2">
                <div className="min-w-0">
                  <p className="truncate text-[12px] font-bold uppercase tracking-[0.06em] text-ink-soft">
                    {s.name}
                  </p>
                  <p className="font-mono text-[10px] text-muted">
                    {done}/{secItems.length} item{secItems.length === 1 ? "" : "s"} · hidden
                  </p>
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  <button
                    onClick={() => void setSectionActive(dealId, s.id, true).catch(alertErr)}
                    className="btn-secondary text-xs"
                  >
                    Reactivate
                  </button>
                  <button
                    onClick={() => {
                      const msg =
                        secItems.length === 0
                          ? `Permanently delete the empty section "${s.name}"?`
                          : `Permanently delete "${s.name}" AND its ${secItems.length} item(s) (${done} completed)?\n\nThis removes them for good.`;
                      if (!window.confirm(msg)) return;
                      void deleteSection(dealId, s.id, {
                        withItems: true,
                        confirmedItemIds: secItems.map((i) => i.id),
                      }).catch(alertErr);
                    }}
                    className="font-mono text-[11px] text-muted hover:text-flag"
                    title="Delete permanently"
                  >
                    delete
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
